import { Spinner } from "@fluentui/react-components";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedTag } from "../../contexts/SelectedTagContext";
import { useTagsGet } from "../../hooks/useTags";
import { FlatVsaaTag } from "../../models/VsaaTag";
import { TagCollectionTree } from "./TagCollectionTree";
interface TagCollectionEditorProps {
  tagCollectionId: string;
  initialSelectedTagId?: string;
  onInitialTagSelected?: () => void;
}

export const TagCollectionEditor = (props: TagCollectionEditorProps) => {
  const { tagCollectionId, initialSelectedTagId, onInitialTagSelected } = props;
  const [userOpenItems, setUserOpenItems] = useState<string[]>([]);

  const {
    data: tags,
    isLoading: isLoadingTags,
    isRefetching: isRefetchingTags,
    error: getTagsError,
  } = useTagsGet(tagCollectionId);

  const tagContext = useSelectedTag();

  const initialOpenItems = useMemo(() => {
    if (!initialSelectedTagId || !tags) {
      return [];
    }

    const initialSelectedTag = tags.find(
      (tag) => tag.id === initialSelectedTagId
    );
    return initialSelectedTag?.ancestorIds ?? [];
  }, [initialSelectedTagId, tags]);

  const selectedTagAncestors = tagContext?.selectedTag?.ancestorIds;

  const openItems = useMemo(() => {
    const combined = new Set([...userOpenItems, ...initialOpenItems]);

    // Auto-expand ancestors of the selected tag so it's visible
    if (selectedTagAncestors) {
      selectedTagAncestors.forEach((id) => combined.add(id));
    }

    return Array.from(combined);
  }, [initialOpenItems, userOpenItems, selectedTagAncestors]);

  const currentTags = useMemo<FlatVsaaTag[]>(() => {
    if (isLoadingTags || isRefetchingTags || !tags) {
      return [];
    }

    const selectedTagId = tagContext?.selectedTag?.id;

    return tags.filter(
      (t) =>
        !t.parentId ||
        openItems.indexOf(t.parentId) !== -1 ||
        selectedTagId === t.parentId // So sorting etc will work properly we always also have the selected tags children in the current set.
    );
  }, [
    tags,
    isLoadingTags,
    openItems,
    tagContext?.selectedTag?.id,
    isRefetchingTags,
  ]);

  const { t } = useTranslation();

  useEffect(() => {
    if (!initialSelectedTagId || !tags || isLoadingTags) return;

    const tag = tags.find((t) => t.id === initialSelectedTagId);
    if (tag) {
      tagContext?.setSelectedTag(tag);
    }
    onInitialTagSelected?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedTagId, tags, isLoadingTags]);

  useEffect(() => {
    if (!tags || !tagContext?.selectedTag) return;

    const updatedTag = tags.find(
      (tag) => tag.id === tagContext?.selectedTag?.id
    );

    // If the tag exists and any property is different, update the selectedTag
    if (updatedTag) {
      const selectedTag = tagContext.selectedTag;

      const hasChanges = Object.keys(tagContext.selectedTag).some(
        (key) =>
          selectedTag[key as keyof FlatVsaaTag] !==
          updatedTag[key as keyof FlatVsaaTag]
      );

      if (hasChanges) {
        tagContext.setSelectedTag(updatedTag);
      }
    }
  }, [tags, tagContext?.selectedTag, tagContext]);

  if (!tags || isLoadingTags) {
    return <Spinner>{t("loadingTags")}</Spinner>;
  }

  if (getTagsError) {
    throw getTagsError;
  }

  return (
    <TagCollectionTree
      tagCollectionId={tagCollectionId}
      tags={currentTags}
      allTags={tags}
      openItems={openItems}
      isLoading={isLoadingTags || isRefetchingTags}
      onOpenItemsChange={setUserOpenItems}
    />
  );
};
